import { Stack, Box, Typography, Paper } from "@mui/material";
import type { PlatformShowQueryType } from "@/types/graphql/admin.generated";
import { CustomCellDate } from "@/shared/components";
import { SudoButton } from "@/admin/shared/components/SudoButton";
import { PlatformStreamingSettingsCard } from "./PlatformStreamingSettingsCard";
import { PlatformBunnySettingsCard } from "./PlatformBunnySettingsCard";
import { PlatformStripeBillingCard } from "./PlatformStripeBillingCard";

interface PlatformDetailsProps {
  platform: PlatformShowQueryType["platform"];
}

export function PlatformDetails({ platform }: PlatformDetailsProps) {
  if (!platform) {
    return (
      <Paper>
        <Typography variant="h6" color="error">
          Платформа не найдена
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h5">{platform.title}</Typography>

        <Typography variant="h6">Системная информация</Typography>

        <Box>
          <Typography variant="body2" color="text.secondary">
            ID
          </Typography>
          <Typography variant="body1">{platform.id}</Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            Slug
          </Typography>
          <Typography variant="body1">{platform.slug}</Typography>
        </Box>

        {platform.host && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Домен
            </Typography>
            <Typography variant="body1">
              <a href={platform.url} target="_blank" rel="noreferrer noopener">
                {platform.host}
              </a>
            </Typography>
          </Box>
        )}

        <Box>
          <Typography variant="body2" color="text.secondary">
            Опубликована
          </Typography>
          <Typography variant="body1">
            {platform.published ? "Да" : "Нет"}
          </Typography>
          {!platform.published && platform.host && (
            <Typography variant="caption" color="text.secondary">
              Основной домен откроется после публикации, сейчас витрина доступна
              по технической ссылке
            </Typography>
          )}
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            Тех. ссылка
          </Typography>
          <Typography variant="body1">
            <a
              href={platform.slugUrl}
              target="_blank"
              rel="noreferrer noopener"
            >
              {platform.slugHost}
            </a>
          </Typography>
        </Box>

        {platform.sudoUrl && (
          <Box>
            <SudoButton href={platform.sudoUrl} label="Кабинет организации" />
          </Box>
        )}

        <Box>
          <Typography variant="body2" color="text.secondary">
            Статус
          </Typography>
          <Typography variant="body1">{platform.status}</Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary">
            Дата создания
          </Typography>
          <CustomCellDate date={platform.createdAt} />
        </Box>

        {platform.updatedAt && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Дата обновления
            </Typography>
            <CustomCellDate date={platform.updatedAt} />
          </Box>
        )}

        {platform.deletedAt && (
          <Box>
            <Typography variant="body2" color="text.secondary">
              Дата удаления
            </Typography>
            <CustomCellDate date={platform.deletedAt} />
          </Box>
        )}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <PlatformStreamingSettingsCard
          platformId={platform.id}
          streamingSettings={platform.streamingSettings}
          streamingAvailable={platform.streamingAvailable}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <PlatformBunnySettingsCard
          platformId={platform.id}
          bunnySettings={platform.bunnySettings}
        />
      </Box>

      <Box sx={{ mt: 2 }}>
        <PlatformStripeBillingCard
          platformId={platform.id}
          stripeBillingMode={platform.stripeBillingMode}
        />
      </Box>
    </Paper>
  );
}
